import { useEffect } from 'react';

import type { BoardViews } from '@trello/router';
import { isActiveRoute, RouteId, useRoute } from '@trello/router';
import { SharedState, useSharedState } from '@trello/shared-state';

import { useBoardPageStateQuery } from './BoardPageStateQuery.generated';

export interface BoardPageState {
  boardIdOrShortLink: string | null;
  cardIdOrShortLink: string | null;
  view:
    | 'board'
    | 'calendar'
    | 'calendar-view'
    | 'dashboard'
    | 'map'
    | 'table'
    | 'timeline'
    | null;
}

type BoardSection = BoardViews['view'];

export function isValidBoardView(
  view: BoardSection,
): view is NonNullable<BoardPageState['view']> {
  return (
    view === 'board' ||
    view === 'calendar' ||
    view === 'calendar-view' ||
    view === 'dashboard' ||
    view === 'map' ||
    view === 'table' ||
    view === 'timeline'
  );
}

export const boardPageState = new SharedState<BoardPageState>({
  boardIdOrShortLink: null,
  cardIdOrShortLink: null,
  view: 'board',
});

/**
 * Preserves the board ID, card ID and board view (e.g. 'timeline', 'dashboard' etc) when
 * moving between boards and cards. If the board ID is set, it means the user is currently
 * on that board, or a card within that board. If the card ID is set, it means the user is
 * currently on a card. If the view is set, it means the user is either currently on that
 * view, or viewing a card with that view in the background.
 *
 * When a user navigates away from a board or card entirely, these values will be reset to
 * their defaults and will not be restored when returning to the board or card.
 */
export const useBoardPageState = () => {
  const [currentBoardPageState, setBoardPageState] =
    useSharedState(boardPageState);
  const route = useRoute<RouteId.BOARD | RouteId.CARD>();

  useEffect(() => {
    return () => {
      boardPageState.reset();
    };
  }, []);

  const { data } = useBoardPageStateQuery({
    skip: currentBoardPageState.cardIdOrShortLink === null,
    variables: {
      cardId: currentBoardPageState.cardIdOrShortLink ?? '',
    },
    fetchPolicy: 'cache-only',
    waitOn: ['PreloadCard'],
  });

  const card = data?.card;

  useEffect(() => {
    if (card && !currentBoardPageState.boardIdOrShortLink) {
      setBoardPageState({
        boardIdOrShortLink: card.idBoard,
      });
    }
  }, [setBoardPageState, card, currentBoardPageState.boardIdOrShortLink]);

  useEffect(() => {
    if (isActiveRoute(route, RouteId.BOARD)) {
      const validBoardView = isValidBoardView(route.params.view)
        ? route.params.view
        : boardPageState.value.view;
      setBoardPageState({
        // We don't want to persist all views from the route onto the board page state. If the view is more like
        // an overlay (e.g. Butler, Power-Ups and Member Activity) then we don't want to persist it. We only want
        // to persist views that are more like a page (e.g. Calendar, Dashboard, Table, Timeline). This allows us
        // to correctly navigate back to the previous view when overlays are closed, or when navigating back to
        // a card.
        view: validBoardView,
        boardIdOrShortLink: route.params.shortLink,
        cardIdOrShortLink: null,
      });
    } else if (isActiveRoute(route, RouteId.CARD)) {
      setBoardPageState({ cardIdOrShortLink: route.params.shortLink });
    } else {
      setBoardPageState({
        view: null,
        boardIdOrShortLink: null,
        cardIdOrShortLink: null,
      });
    }
  }, [route, setBoardPageState]);

  return currentBoardPageState;
};
